import { clarity as reactMicrosoftClarity } from 'react-microsoft-clarity';
import Cookies from 'universal-cookie';

const ENV = process.env.NEXT_PUBLIC_BUILD_ENV || process.env.NODE_ENV;
const claritySiteId = process.env.NEXT_PUBLIC_CLARITY_SITE_ID;

type ClarityParams = Array<string | boolean>;

interface User {
  userId: number;
  firstName?: string;
  lastName?: string;
}

// Get cookie consent
function getConsentCookie() {
  if (typeof window === 'undefined') return null;
  const cookies = new Cookies();
  return cookies.get('manager_consent');
}

// Apply a Clarity command safely
function applyClarityCommand(...params: ClarityParams) {
  if (!claritySiteId) {
    throw new Error('Clarity site ID is not defined');
  }

  // Check if Clarity is not initialised and initialise it
  if (!window.clarity) {
    reactMicrosoftClarity.init(claritySiteId);
    // If Clarity is still not initialised, throw an error
    if (!window.clarity) {
      throw new Error('Clarity is not initialised');
    }
  }

  window.clarity.apply?.(undefined, params);
}

// Check if Clarity has started
function hasStarted() {
  return typeof window.clarity === 'function' || typeof window.clarity === 'object';
}

// Upgrade prioritizes specific session types for recording beyond the default rules.
function upgrade(reason: string) {
  applyClarityCommand('upgrade', reason);
}

// Set a tag in Clarity
function setTag(key: string, value: string) {
  if (!key || value == null) return;
  applyClarityCommand('set', key, value);
}

// Set multiple tags in Clarity
function setTags(tags: { [key: string]: string }) {
  if (!tags || typeof tags !== 'object') return;
  Object.entries(tags).forEach(([key, value]) => {
    if (key && value != null) {
      applyClarityCommand('set', key, value);
    }
  });
}

// Identify a user in Clarity
function identify(user: User) {
  if (!user?.userId) return;

  let clarityIdentify = 'user-unknown';

  if (user.firstName && user.lastName) {
    const firstName = user.firstName.replace(/\s/g, '-');
    const lastName = user.lastName.replace(/\s/g, '-');
    clarityIdentify = `${firstName}-${lastName}`;
  } else {
    clarityIdentify = `userID-${user.userId.toString()}`;
  }
  setTag('lushUserId', user?.userId?.toString());

  applyClarityCommand('identify', `${ENV}-${clarityIdentify}`);
}

// Initialise Clarity - only if consent cookie exists
function init() {
  const consentCookie = getConsentCookie();
  const hasConsent = consentCookie === 'true';

  if (!hasConsent) {
    console.warn('Clarity not initialized: no manager_consent cookie.');
    return;
  }

  if (!claritySiteId) throw new Error('Clarity site ID is not defined');

  try {
    reactMicrosoftClarity.init(claritySiteId);
    reactMicrosoftClarity.consent(true);
  } catch (error) {
    console.warn('Clarity initialization failed:', error);
  }
}

// Stop tracking
function stopTracking() {
  try {
    if (typeof reactMicrosoftClarity.stop === 'function') {
      reactMicrosoftClarity.stop();
    }
  } catch (error) {
    console.error('Failed to stop Clarity tracking:', error);
  }
}

// Explicit consent handler
function consent(value: boolean) {
  if (typeof value !== 'boolean') return;
  try {
    if (typeof reactMicrosoftClarity.consent === 'function') {
      reactMicrosoftClarity.consent(value);
    }
  } catch (error) {
    console.error('Failed to set Clarity consent:', error);
  }
}

// Export clarity functions
export const clarity = {
  init,
  hasStarted,
  identify,
  upgrade,
  setTag,
  setTags,
  stopTracking,
  consent
};
